import { FormEvent, useState } from "react";
import content from "@/content/site-content.json";
import type { SiteContent } from "@/types/content";

const { contact } = content as SiteContent;

type Status = "idle" | "sending" | "sent" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());

    // Enkel honeypot mot spam-roboter — feltet er skjult for mennesker.
    if (data.website) {
      setStatus("sent");
      return;
    }

    setStatus("sending");
    setErrorMessage("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Noe gikk galt på serveren.");
      }

      setStatus("sent");
      form.reset();
    } catch (err) {
      setStatus("error");
      setErrorMessage(
        err instanceof Error ? err.message : "Klarte ikke å sende meldingen."
      );
    }
  }

  return (
    <section id="kontakt" className="section contact">
      <div className="container contact__inner">
        <div>
          <p className="eyebrow">{contact.eyebrow}</p>
          <h2>{contact.title}</h2>
          <p>{contact.intro}</p>
          <p className="contact__leader">
            Meldingen går til <strong>{contact.leaderName}</strong>,{" "}
            {contact.leaderRole}.
          </p>
          <p className="contact__response-note">{contact.responseNote}</p>
        </div>

        {status === "sent" ? (
          <div className="contact__success" role="status">
            <h3>Meldingen er sendt</h3>
            <p>
              Takk for at du tok kontakt. Vi svarer deg så snart vi kan,
              vanligvis innen 2–3 virkedager.
            </p>
          </div>
        ) : (
          <form className="contact__form" onSubmit={handleSubmit} noValidate>
            <div className="field">
              <label htmlFor="name">Navn</label>
              <input id="name" name="name" type="text" required autoComplete="name" />
            </div>
            <div className="field">
              <label htmlFor="email">E-post</label>
              <input id="email" name="email" type="email" required autoComplete="email" />
            </div>
            <div className="field">
              <label htmlFor="message">Melding</label>
              <textarea id="message" name="message" rows={5} required />
            </div>

            {/* Honeypot – usynlig for mennesker, fanger opp spam-roboter */}
            <div className="field field--hidden" aria-hidden="true">
              <label htmlFor="website">La stå tomt</label>
              <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
            </div>

            {status === "error" && (
              <p className="contact__error" role="alert">
                Meldingen ble ikke sendt: {errorMessage} Prøv gjerne igjen, eller
                skriv direkte til {content.footer.email}.
              </p>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={status === "sending"}
            >
              {status === "sending" ? "Sender…" : "Send melding"}
            </button>
          </form>
        )}
      </div>

      <style>{`
        .contact__inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-4);
          align-items: start;
        }
        .contact__leader {
          font-size: 0.95rem;
        }
        .contact__response-note {
          color: var(--color-ink-soft);
          font-size: 0.9rem;
        }
        .contact__form {
          background: #fff;
          border: 1px solid var(--color-line);
          border-radius: var(--radius-lg);
          padding: var(--space-3);
          display: grid;
          gap: var(--space-2);
        }
        .field {
          display: grid;
          gap: 0.35rem;
        }
        .field--hidden {
          position: absolute;
          width: 1px;
          height: 1px;
          overflow: hidden;
          opacity: 0;
        }
        .field label {
          font-weight: 600;
          font-size: 0.9rem;
        }
        .field input,
        .field textarea {
          padding: 0.7rem 0.9rem;
          border-radius: 10px;
          border: 1.5px solid var(--color-line);
          background: var(--color-cream);
        }
        .field input:focus,
        .field textarea:focus {
          border-color: var(--color-clay);
        }
        .contact__error {
          background: #fbe8e2;
          color: #7a2e1e;
          border-radius: 10px;
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
          margin: 0;
        }
        .contact__success {
          background: var(--color-paper);
          border-radius: var(--radius-lg);
          padding: var(--space-3);
        }
        @media (max-width: 860px) {
          .contact__inner {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
}
